import * as React from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ConfirmDialog, useAuth, useToast } from "@ozpanel/ui";

// Deleting your own feed (DELETE /me/notifications, Oz 2026-10-25: "delete the notifications, don't
// keep them logs, start fresh"). One hook for the bell and the feed page, so both ask the same
// question and say the same thing back.
//
// The server scopes it exactly as it scopes the feed — this person's rows, never anyone else's —
// and KEEPS the faults that are still open: deleting one would make its next report page again as
// new. The confirmation says so, and the result names how many were kept.

export type ClearMode = "read" | "all";
type Cleared = { deleted: number; kept_open: number; more?: boolean };

export function useClearNotifications() {
  const { client } = useAuth();
  const qc = useQueryClient();
  const { notify } = useToast();
  const [mode, setMode] = React.useState<ClearMode | null>(null);

  const del = useMutation({
    mutationFn: (m: ClearMode) =>
      m === "read"
        ? client.apiFetch<Cleared>("/me/notifications?read=true", { method: "DELETE" })
        : client.apiFetch<Cleared>("/me/notifications", { method: "DELETE" }),
    onSuccess: (d) => {
      notify({
        kind: "success",
        title: `Deleted ${d.deleted} notification${d.deleted === 1 ? "" : "s"}`,
        body:
          (d.kept_open > 0 ? `${d.kept_open} still-open fault${d.kept_open === 1 ? " was" : "s were"} kept — they go once announced over.` : "") +
          (d.more ? " More remain; clear again." : "") || undefined,
      });
      void qc.invalidateQueries({ queryKey: ["me-notifications"] });
      void qc.invalidateQueries({ queryKey: ["notif-feed"] });
      setMode(null);
    },
    onError: (e: unknown) =>
      notify({ kind: "error", title: "Could not delete them", body: e instanceof Error ? e.message : String(e) }),
  });

  const dialog = (
    <ConfirmDialog
      open={mode != null}
      onOpenChange={(o) => {
        if (!o) setMode(null);
      }}
      title={mode === "all" ? "Delete all your notifications?" : "Delete your read notifications?"}
      body={
        <>
          {mode === "all"
            ? "Every notification in your feed is deleted, read or not. "
            : "The notifications you have read are deleted; unread ones stay. "}
          Faults that are still open are kept — they are what is wrong now, and deleting one would page
          you again as new on its next report. This cannot be undone.
        </>
      }
      confirmLabel="Delete"
      destructive
      pending={del.isPending}
      onConfirm={() => {
        if (mode) del.mutate(mode);
      }}
    />
  );
  return { request: setMode, dialog };
}
